import { useState } from 'react';
import palette from "../../../../utilities/palette";
import { asilRatingOptions, trueFalseDropdownOptions } from "../../../../utilities/dropDownOptions";
import { getItemByValueInDropdownOptions } from "../../../../utilities/utilityFunctions";
import { Accordion, AccordionDetails, AccordionSummary, ButtonBase } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { TextField } from "../../formFields/TextField";
import { SelectField } from "../../formFields/SelectField";
import { useDataContext } from "../../../DataProvider/DataContext";
import { MultiValueTextField } from "../../formFields/MultiValuesTextField";
import { ClusterList } from './ClusterList';
import { validateVariantValue } from "../../../../utilities/formValidation";
import { extractVariantErrors } from '../../../../utilities/validationErrorExtraction';
import { checkIfFieldIsRequiredPerArchitecture } from '../../../../utilities/architectureTypeDataProcess';
import { FlexColumnDiv, FlexRowDiv, HeaderDiv, StepperContentBodyDiv } from '../../../styledComponents/Components.styles';
import LabelField from "../../formFields/LabelField";
import { textHints } from '../../../../utilities/hintText';


export function VariantList({
    moduleInformation,
    machineList, machineIndex, setMachineList, machineListErrors, setMachineListErrors,
    projectKey, moduleName
}) {

    // tracks/handles what accordion element is expanded
    const [expanded, setExpanded] = useState(`panel${machineIndex}-0`);

    const handleChange = (panel) => (event, newExpanded) => {
        setExpanded(newExpanded ? panel : false);
    };

    const {ECUData, architectureType, dynamicSelectOptionsMap} = useDataContext();

    // Derive variantDescription directly during render
    const machineToVariantData = ECUData.current.machineToVariantData || {};
    const variantDescription = (() => {
        const machineToVariantDatKey = Object.keys(machineToVariantData);
        for (const machineId of machineToVariantDatKey) {
            const machineData = machineToVariantData[machineId];
            if (machineData.machineName === machineList[machineIndex].machineName) {
                const variantsKey = Object.keys(machineData.variants || {});
                for (const variantId of variantsKey) {
                    const variantData = machineData.variants[variantId];
                    if (variantData.variantName === machineList[machineIndex].variantList[0].variantName) {
                        return variantData.variantDescription;
                    }
                }
            }
        }
        return "N/A";
    })();

    // Derive error lists directly during render
    const { fieldsWithErrorList, clusterWithErrorList } = extractVariantErrors(machineListErrors, machineIndex);

    return (
    
        <>
            {machineList[machineIndex].variantList.map((variantEntry, variantIndex) => (
                <HeaderDiv
                    key={`variantList_${variantEntry.variantName}_${variantEntry.logicalDiagAddress}`}
                    style={{margin: '15px'}}
                >
                    <Accordion slotProps={{ transition: { unmountOnExit: true } }} expanded={expanded === `panel${machineIndex}-${variantIndex}`} onChange={handleChange(`panel${machineIndex}-${variantIndex}`)}
                        id={`panel${machineIndex}-${variantIndex}`}
                        sx={{ 
                            background: 'transparent',
                            padding: '20px 0',
                            boxShadow: 'none',

                        }}
                    >
                        {/* Top bar of accordion element */}
                        <ButtonBase sx={{width: '100%'}}>
                            <AccordionSummary
                                aria-controls={`panel${machineIndex}-${variantIndex}-content`}
                                id={`panel${machineIndex}-${variantIndex}-header`}
                                sx={{
                                    ":hover": {
                                        backgroundColor: palette.header.main
                                    },
                                    borderRadius: '10px'
                                }}
                            >
                                {expanded === `panel${machineIndex}-${variantIndex}` && <ExpandMoreIcon sx={{fontSize: '30px'}}/>}

                                <div style={{ display: "flex", flexDirection: "column", alignSelf: "center", width: "100%", cursor: 'pointer' }}>
                                    <FlexColumnDiv style={{
                                        borderLeft: `4px solid ${palette.main}`,
                                        borderRadius: '4px',
                                        minHeight: '100px',
                                        padding: '10px 20px 10px 20px',
                                        gap: '5px',
                                    }}>
                                        <span style={{ fontWeight: "650", fontSize: "20px", paddingLeft: '5px' }}>
                                            Variant {`${variantIndex + 1}`}
                                        </span>

                                    {expanded === `panel${machineIndex}-${variantIndex}` ?
                                    
                                        <FlexColumnDiv style={{gap: '5px'}}> 
                                            <LabelField
                                                label={"Variant Name"}
                                                value={variantEntry.variantName}
                                                cursor="pointer"
                                            />
                                            <LabelField
                                                label={"Variant Description"}
                                                value={variantDescription}
                                                cursor="pointer"
                                            />
                                        </FlexColumnDiv>
                                        :
                                        <FlexRowDiv style={{gap: '100px',}}>
                                            <LabelField
                                                label={"Variant Name"}
                                                value={variantEntry.variantName}
                                                cursor="pointer"
                                            />
                                            <LabelField
                                                label={"Variant Description"}
                                                value={variantDescription}
                                                cursor="pointer"
                                            />
                                        </FlexRowDiv>
                                    }


                                        {fieldsWithErrorList[variantIndex] && fieldsWithErrorList[variantIndex].length > 0 &&
                                            <span style={{color: palette.stepper.error, paddingTop: '5px', marginLeft: '5px'}}>Validation errors found in one or more input fields</span>                                    
                                        }
                                        {clusterWithErrorList[variantIndex] && clusterWithErrorList[variantIndex].length > 0 &&
                                                <span style={{color: palette.stepper.error, paddingTop: '5px', marginLeft: '5px'}}>Error in {clusterWithErrorList[variantIndex]?.join(", ")}</span>
                                        }
                                    </FlexColumnDiv>                                
                                </div>

                            </AccordionSummary>
                        </ButtonBase>


                        {/* Internal content of accordion element */}
                        <AccordionDetails>
                        <FlexColumnDiv style={{marginLeft: '20px', marginBottom: '20px'}}>

                            <div style={{color: palette.main, fontWeight: '600', fontSize: '24px', margin: '20px 0'}}>Variant Attributes</div>
                            <StepperContentBodyDiv style={{width: '1240px', alignSelf: 'center', marginBottom: '20px', alignItems: 'center', flexDirection: 'column'}}>
                                <FlexRowDiv style={{gap: '36px'}}>
                                    <SelectField
                                        label="OTA Capable"
                                        value={getItemByValueInDropdownOptions(trueFalseDropdownOptions, variantEntry.otaCapable)}
                                        required={true}
                                        options={trueFalseDropdownOptions}
                                        onChange={(selectedOptions) => setMachineList(prevMachineList => {
                                            prevMachineList[machineIndex].variantList[variantIndex].otaCapable = selectedOptions.value;
                                            return [...prevMachineList];
                                        })}
                                        onRevert={() => setMachineList(prev => {
                                            const updatedMachineList = structuredClone(prev);
                                            updatedMachineList[machineIndex].variantList[variantIndex].otaCapable = ECUData.current.prefilledJSONContent.machineList[machineIndex].variantList[variantIndex].otaCapable;
                                            return updatedMachineList;
                                        })}
                                        textHint={textHints.otaCapable}
                                        idInput={`fsc_otaCapable_${machineIndex}_${variantIndex}`}
                                        nameInput={`otaCapable_${machineIndex}_${variantIndex}`}
                                        pathToField={`machineList.0.variantList.0.otaCapable`} 
                                        stylingVariant="body"
                                        isPrefilledValueChanged={variantEntry.otaCapable !== ECUData.current.prefilledJSONContent.machineList[machineIndex].variantList[variantIndex].otaCapable}
                                        
                                        />
                                    <MultiValueTextField
                                        label="Communication Protocol"
                                        value={
                                            Array.isArray(variantEntry.commsProtocol) ?
                                                variantEntry.commsProtocol.map(item => ({ value: item, label: item })) :
                                                []
                                        }
                                        required={checkIfFieldIsRequiredPerArchitecture(
                                            `machineList.0.variantList.0.commsProtocol`,
                                            architectureType
                                        )}
                                        onChange={(selectedOptions) => {
                                            const selectedValues = selectedOptions.map(option => option.value);
                                            setMachineList(prevMachineList => {
                                                const updatedMachineList = structuredClone(prevMachineList);
                                                updatedMachineList[machineIndex].variantList[variantIndex].commsProtocol = selectedValues
                                                return updatedMachineList;
                                            });
                                            if (checkIfFieldIsRequiredPerArchitecture(
                                                `machineList.0.variantList.0.commsProtocol`,
                                                architectureType
                                            )) {

                                                validateVariantValue(
                                                    machineIndex,
                                                    variantIndex,
                                                    'commsProtocol',
                                                    selectedValues,
                                                    setMachineListErrors,
                                                );
                                            }
                                        }}
                                        onRevert={() => {
                                            setMachineList(prev => {
                                                const updatedMachineList = structuredClone(prev);
                                                updatedMachineList[machineIndex].variantList[variantIndex].commsProtocol = ECUData.current.prefilledJSONContent.machineList[machineIndex].variantList[variantIndex].commsProtocol;
                                                return updatedMachineList;
                                            });
                                            if (checkIfFieldIsRequiredPerArchitecture(
                                                `machineList.0.variantList.0.commsProtocol`,
                                                architectureType
                                            )) {
                                                validateVariantValue(
                                                    machineIndex,
                                                    variantIndex,
                                                    'commsProtocol',
                                                    ECUData.current.prefilledJSONContent.machineList[machineIndex].variantList[variantIndex].commsProtocol,
                                                    setMachineListErrors,
                                                );
                                            }
                                        }}
                                        placeholder="Enter values (press Enter after each)"
                                        pathToField={`machineList.0.variantList.0.commsProtocol`}
                                        errorMessage={machineListErrors[machineIndex]?.variantListErrors[variantIndex]?.commsProtocolValidationError} 
                                        stylingVariant="body"
                                        isPrefilledValueChanged={JSON.stringify(variantEntry.commsProtocol) !== JSON.stringify(ECUData.current.prefilledJSONContent.machineList[machineIndex].variantList[variantIndex].commsProtocol)}
                                        textHint={textHints.commsProtocol}
                                    />
                                </FlexRowDiv>

                                <FlexRowDiv style={{gap: '36px'}}>
                                    <TextField
                                        label="flashCapability"
                                        value={variantEntry.flashCapability}
                                        required={checkIfFieldIsRequiredPerArchitecture(
                                            `machineList.0.variantList.0.flashCapability`,
                                            architectureType
                                        )}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setMachineList(prevMachineList => {
                                                const updatedMachineList = structuredClone(prevMachineList);
                                                updatedMachineList[machineIndex].variantList[variantIndex].flashCapability = value;
                                                return updatedMachineList;
                                            });

                                            if (checkIfFieldIsRequiredPerArchitecture(
                                                `machineList.0.variantList.0.flashCapability`,
                                                architectureType
                                            )) {
                                                validateVariantValue(
                                                    machineIndex,
                                                    variantIndex,
                                                    'flashCapability',
                                                    value,
                                                    setMachineListErrors,
                                                );
                                            }
                                        }}
                                        onRevert={() => {
                                            setMachineList(prev => {
                                                const updatedMachineList = structuredClone(prev);
                                                updatedMachineList[machineIndex].variantList[variantIndex].flashCapability =ECUData.current.prefilledJSONContent.machineList[machineIndex].variantList[variantIndex].flashCapability;
                                                return updatedMachineList;
                                            });
                                            if (checkIfFieldIsRequiredPerArchitecture(
                                                `machineList.0.variantList.0.flashCapability`,
                                                architectureType
                                            )) {
                                                validateVariantValue(
                                                    machineIndex,
                                                    variantIndex,
                                                    'flashCapability',
                                                    ECUData.current.prefilledJSONContent.machineList[machineIndex].variantList[variantIndex].flashCapability,
                                                    setMachineListErrors,
                                                );
                                            }
                                        }}
                                        placeholder="Enter Flash Capability"
                                        pathToField={`machineList.0.variantList.0.flashCapability`}
                                        errorMessage={machineListErrors[machineIndex]?.variantListErrors[variantIndex]?.flashCapabilityValidationError}
                                        idInput={`fsc_flashCapability_${machineIndex}_${variantIndex}`}
                                        nameInput={`flashCapability_${machineIndex}_${variantIndex}`} 
                                        stylingVariant="body"
                                        isPrefilledValueChanged={variantEntry.flashCapability !== ECUData.current.prefilledJSONContent.machineList[machineIndex].variantList[variantIndex].flashCapability}
                                        textHint={textHints.flashCapability}
                                    />
                                    <TextField
                                        label="Public Topology"
                                        value={variantEntry.publicTopology}
                                        required={
                                            checkIfFieldIsRequiredPerArchitecture(
                                                `machineList.0.variantList.0.publicTopology`,
                                                architectureType
                                            )
                                        }
                                        onChange={(e) => {
                                            setMachineList(prevMachineList => {
                                                const updatedMachineList = structuredClone(prevMachineList);
                                                updatedMachineList[machineIndex].variantList[variantIndex].publicTopology = e.target.value;
                                                return updatedMachineList;
                                            });
                                            if (checkIfFieldIsRequiredPerArchitecture(
                                                `machineList.0.variantList.0.publicTopology`,
                                                architectureType
                                            )) {
                                                validateVariantValue(
                                                    machineIndex,
                                                    variantIndex,
                                                    'publicTopology',
                                                    e.target.value,
                                                    setMachineListErrors,
                                                );
                                            }
                                        }}
                                        onRevert={() => {
                                            setMachineList(prev => {
                                                const updatedMachineList = structuredClone(prev);
                                                updatedMachineList[machineIndex].variantList[variantIndex].publicTopology = ECUData.current.prefilledJSONContent.machineList[machineIndex].variantList[variantIndex].publicTopology;
                                                return updatedMachineList;
                                            });
                                            if (checkIfFieldIsRequiredPerArchitecture(
                                                `machineList.0.variantList.0.publicTopology`,
                                                architectureType
                                            )) {
                                                validateVariantValue(
                                                    machineIndex,
                                                    variantIndex,
                                                    'publicTopology',
                                                    ECUData.current.prefilledJSONContent.machineList[machineIndex].variantList[variantIndex].publicTopology,
                                                    setMachineListErrors,
                                                );
                                            }
                                        }}
                                        placeholder="Enter Public Topology"
                                        pathToField={`machineList.0.variantList.0.publicTopology`}
                                        idInput={`fsc_publicTopology_${machineIndex}_${variantIndex}`}
                                        nameInput={`publicTopology_${machineIndex}_${variantIndex}`} 
                                        stylingVariant="body"
                                        isPrefilledValueChanged={variantEntry.publicTopology !== ECUData.current.prefilledJSONContent.machineList[machineIndex].variantList[variantIndex].publicTopology}
                                        textHint={textHints.publicTopology}
                                    />
                                </FlexRowDiv>

                                <FlexRowDiv style={{gap: '36px'}}>
                                    <SelectField
                                        label="ASIL Rating"
                                        value={getItemByValueInDropdownOptions(asilRatingOptions, variantEntry.asilRating)}
                                        required={checkIfFieldIsRequiredPerArchitecture(
                                            `machineList.0.variantList.0.asilRating`,
                                            architectureType
                                        )}
                                        options={asilRatingOptions}
                                        placeholder="--Please Select--"
                                        onChange={(selectedOptions) => {
                                            setMachineList(prevMachineList => {
                                                const updatedMachineList = structuredClone(prevMachineList);
                                                updatedMachineList[machineIndex].variantList[variantIndex].asilRating = selectedOptions.value;
                                                return updatedMachineList;
                                            });
                                            if (checkIfFieldIsRequiredPerArchitecture(
                                                `machineList.0.variantList.0.asilRating`,
                                                architectureType
                                            )) {
                                                validateVariantValue(
                                                    machineIndex,
                                                    variantIndex,
                                                    'asilRating',
                                                    selectedOptions.value,
                                                    setMachineListErrors,
                                                );
                                            }
                                        }}
                                        onRevert={() => {
                                            setMachineList(prev => {
                                                const updatedMachineList = structuredClone(prev);
                                                updatedMachineList[machineIndex].variantList[variantIndex].asilRating = ECUData.current.prefilledJSONContent.machineList[machineIndex].variantList[variantIndex].asilRating;
                                                return updatedMachineList;
                                            });
                                            if (checkIfFieldIsRequiredPerArchitecture(
                                                `machineList.0.variantList.0.asilRating`,
                                                architectureType
                                            )) {
                                                validateVariantValue(
                                                    machineIndex,
                                                    variantIndex,
                                                    'asilRating',
                                                    ECUData.current.prefilledJSONContent.machineList[machineIndex].variantList[variantIndex].asilRating,
                                                    setMachineListErrors,
                                                );
                                            }
                                        }}
                                        pathToField={`machineList.0.variantList.0.asilRating`}
                                        errorMessage={machineListErrors[machineIndex]?.variantListErrors[variantIndex]?.asilRatingValidationError}
                                        idInput={`fsc_asilRating_${machineIndex}_${variantIndex}`}
                                        nameInput={`asilRating_${machineIndex}_${variantIndex}`}
                                        stylingVariant="body"
                                        isPrefilledValueChanged={variantEntry.asilRating !== ECUData.current.prefilledJSONContent.machineList[machineIndex].variantList[variantIndex].asilRating}
                                        textHint={textHints.asilRating}
                                    />
                                    <TextField
                                        label="Private Topology"
                                        value={variantEntry.privateTopology}
                                        required={checkIfFieldIsRequiredPerArchitecture(
                                            `machineList.0.variantList.0.privateTopology`,
                                            architectureType
                                        )}
                                        onChange={(e) => {
                                            setMachineList(prevMachineList => {
                                                const updatedMachineList = structuredClone(prevMachineList);
                                                updatedMachineList[machineIndex].variantList[variantIndex].privateTopology = e.target.value;
                                                return updatedMachineList;
                                            });
                                            if (checkIfFieldIsRequiredPerArchitecture(
                                                `machineList.0.variantList.0.privateTopology`,
                                                architectureType
                                            )) {
                                                validateVariantValue(
                                                    machineIndex,
                                                    variantIndex,
                                                    'privateTopology',
                                                    e.target.value,
                                                    setMachineListErrors,
                                                );
                                            }
                                        }}
                                        onRevert={() => {
                                            setMachineList(prev => {
                                                const updatedMachineList = structuredClone(prev);
                                                updatedMachineList[machineIndex].variantList[variantIndex].privateTopology = ECUData.current.prefilledJSONContent.machineList[machineIndex].variantList[variantIndex].privateTopology;
                                                return updatedMachineList;
                                            });
                                            if (checkIfFieldIsRequiredPerArchitecture(
                                                `machineList.0.variantList.0.privateTopology`,
                                                architectureType
                                            )) {
                                                validateVariantValue(
                                                    machineIndex,
                                                    variantIndex,
                                                    'privateTopology',
                                                    ECUData.current.prefilledJSONContent.machineList[machineIndex].variantList[variantIndex].privateTopology,
                                                    setMachineListErrors,
                                                );
                                            }
                                        }}
                                        placeholder="Enter Private Topology"
                                        pathToField={`machineList.0.variantList.0.privateTopology`}
                                        errorMessage={machineListErrors[machineIndex]?.variantListErrors[variantIndex]?.privateTopologyValidationError}
                                        idInput={`fsc_privateTopology_${machineIndex}_${variantIndex}`}
                                        nameInput={`privateTopology_${machineIndex}_${variantIndex}`} 
                                        stylingVariant="body"
                                        isPrefilledValueChanged={variantEntry.privateTopology !== ECUData.current.prefilledJSONContent.machineList[machineIndex].variantList[variantIndex].privateTopology}
                                        textHint={textHints.privateTopology}
                                    />
                                </FlexRowDiv>

                                <FlexRowDiv style={{gap: '36px'}}>
                                    <SelectField
                                        label = "Supplier"
                                        value={getItemByValueInDropdownOptions(dynamicSelectOptionsMap.supplier, variantEntry.supplier)}
                                        required={checkIfFieldIsRequiredPerArchitecture(
                                            `machineList.0.variantList.0.supplier`,
                                            architectureType
                                        )}
                                        options={dynamicSelectOptionsMap.supplier}
                                        placeholder="--Please Select--"
                                        onChange={(selectedOptions) => {
                                            setMachineList(prevMachineList => {
                                                const updatedMachineList = structuredClone(prevMachineList);
                                                updatedMachineList[machineIndex].variantList[variantIndex].supplier = selectedOptions.value;
                                                return updatedMachineList;
                                            });
                                            if (checkIfFieldIsRequiredPerArchitecture(
                                                `machineList.0.variantList.0.supplier`,
                                                architectureType
                                            )) {
                                                validateVariantValue(
                                                    machineIndex,
                                                    variantIndex,
                                                    'supplier',
                                                    selectedOptions.value,
                                                    setMachineListErrors,
                                                );
                                            }
                                        }}
                                        onRevert={() => {
                                                setMachineList(prev => {
                                                const updatedMachineList = structuredClone(prev);
                                                updatedMachineList[machineIndex].variantList[variantIndex].supplier = ECUData.current.prefilledJSONContent.machineList[machineIndex].variantList[variantIndex].supplier;
                                                return updatedMachineList;
                                            });
                                            if (checkIfFieldIsRequiredPerArchitecture(
                                                `machineList.0.variantList.0.supplier`,
                                                architectureType
                                            )) {
                                                validateVariantValue(
                                                    machineIndex,
                                                    variantIndex,
                                                    'supplier',
                                                    ECUData.current.prefilledJSONContent.machineList[machineIndex].variantList[variantIndex].supplier,
                                                    setMachineListErrors,
                                                );
                                            }
                                        
                                    }}
                                        pathToField="machineList.0.variantList.0.supplier"
                                        errorMessage={machineListErrors[machineIndex]?.variantListErrors[variantIndex]?.supplierValidationError}
                                        idInput={`fsc_supplier_${machineIndex}_${variantIndex}`}
                                        nameInput={`supplier_${machineIndex}_${variantIndex}`}
                                        stylingVariant="body"
                                        textHint={textHints.supplier}
                                    />
                                    <MultiValueTextField
                                        label={"Ethernet Topology"}
                                        value={
                                            Array.isArray(variantEntry.ethernetTopology) ?
                                                variantEntry.ethernetTopology.map(item => ({ value: item, label: item })) :
                                                []
                                        }
                                        required={checkIfFieldIsRequiredPerArchitecture(
                                            `machineList.0.variantList.0.ethernetTopology`,
                                            architectureType
                                        )}
                                        onChange={(selectedOptions) => {
                                            const selectedValues = selectedOptions.map(option => option.value);
                                            setMachineList(prevMachineList => {
                                                const updatedMachineList = structuredClone(prevMachineList);
                                                updatedMachineList[machineIndex].variantList[variantIndex].ethernetTopology = selectedValues;
                                                return updatedMachineList;
                                            });
                                            if (checkIfFieldIsRequiredPerArchitecture(
                                                `machineList.0.variantList.0.ethernetTopology`,
                                                architectureType
                                            )) {
                                                validateVariantValue(
                                                    machineIndex,
                                                    variantIndex,
                                                    'ethernetTopology',
                                                    selectedValues,
                                                    setMachineListErrors,
                                                );
                                            }
                                        }}
                                        onRevert={() => {
                                            setMachineList(prev => {
                                                const updatedMachineList = structuredClone(prev);
                                                updatedMachineList[machineIndex].variantList[variantIndex].ethernetTopology = ECUData.current.prefilledJSONContent.machineList[machineIndex].variantList[variantIndex].ethernetTopology;
                                                return updatedMachineList;
                                            });
                                            if (checkIfFieldIsRequiredPerArchitecture(
                                                `machineList.0.variantList.0.ethernetTopology`,
                                                architectureType
                                            )) {
                                                validateVariantValue(
                                                    machineIndex,
                                                    variantIndex,
                                                    'ethernetTopology',
                                                    ECUData.current.prefilledJSONContent.machineList[machineIndex].variantList[variantIndex].ethernetTopology,
                                                    setMachineListErrors,
                                                );
                                            }
                                        }}
                                        placeholder="Enter values (press Enter after each)"
                                        pathToField={`machineList.0.variantList.0.ethernetTopology`}
                                        errorMessage={machineListErrors[machineIndex]?.variantListErrors[variantIndex]?.ethernetTopologyValidationError}
                                        idInput={`fsc_ethernetTopology_${machineIndex}_${variantIndex}`}
                                        nameInput={`ethernetTopology_${machineIndex}_${variantIndex}`} 
                                        stylingVariant="body"    
                                        isPrefilledValueChanged={JSON.stringify(variantEntry.ethernetTopology) !== JSON.stringify(ECUData.current.prefilledJSONContent.machineList[machineIndex].variantList[variantIndex].ethernetTopology)}
                                        textHint={textHints.ethernetTopology}
                                    />
                                </FlexRowDiv>
                            </StepperContentBodyDiv>

                        

                            <ClusterList
                                moduleInformation={moduleInformation}
                                machineList={machineList}
                                setMachineList={setMachineList}
                                machineIndex={machineIndex}
                                machineListErrors={machineListErrors}
                                setMachineListErrors={setMachineListErrors}
                                variantIndex={variantIndex} 
                                projectKey={projectKey}
                                moduleName={moduleName}
                                />
                        </FlexColumnDiv>  
                        </AccordionDetails>

                    </Accordion>
                </HeaderDiv>
            ))}

        </>)
}